/**
 * Pirate Clash Game - Main Entry Point
 * Three.js + Cannon.js Naval Battle Game
 */

import { Game } from './core/Game.js';
import { UIManager } from './ui/UIManager.js';
import { isMobileDevice } from './utils/device.js';
import { modelLoader } from './systems/ModelLoader.js';

class GameApp {
    constructor() {
        this.game = null;
        this.uiManager = null;
        this.isInitialized = false;
        this.zoomPreventionHandlers = null;
    }

    async init() {
        try {

            // Initialize UI Manager
            this.uiManager = new UIManager();
            this.uiManager.showLoading();

            // Initialize Game Core
            this.game = new Game({
                canvas: document.getElementById('gameCanvas'),
                debug: false
            });

            await this.game.init();

            if (isMobileDevice()) {
                this.preventPageZoom();
                this.setupMobileControls();
            }
            // Setup event listeners
            this.setupEventListeners();

            this.isInitialized = true;

            // 标记初始加载完成，后续模型加载不再显示进度
            modelLoader.finishInitialLoad();

            this.uiManager.hideLoading();
            this.uiManager.showMenu();


        } catch (error) {
            console.error('鉂?Failed to initialize game:', error);
            this.uiManager.showError('Failed to load game. Please refresh the page.');
        }
    }

    setupMobileControls() {
        if (!this.game || !this.uiManager || !this.uiManager.hasJoystick()) return;

        this.uiManager.setJoystickHandler(({ x, y, active }) => {
            if (!this.game || !this.game.inputManager) return;

            if (!this.game.inputManager.isJoystickEnabled()) {
                this.game.inputManager.enableJoystick(true);
            }

            this.game.inputManager.setJoystickVector({ x, y }, active);
        });

        if (this.game.inputManager) {
            this.game.inputManager.enableJoystick(true);
            this.game.inputManager.resetJoystick();
        }

        this.uiManager.hideJoystick();
    }

    preventPageZoom() {
        if (this.zoomPreventionHandlers || typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        const preventPinch = (event) => {
            if (event.touches && event.touches.length > 1) {
                event.preventDefault();
            }
        };

        const preventWheelZoom = (event) => {
            if (event.ctrlKey) {
                event.preventDefault();
            }
        };

        const preventGesture = (event) => {
            event.preventDefault();
        };

        let lastTouchEnd = 0;
        const preventDoubleTapZoom = (event) => {
            const now = Date.now();
            if (now - lastTouchEnd <= 300) {
                event.preventDefault();
            }
            lastTouchEnd = now;
        };

        window.addEventListener('touchstart', preventPinch, { passive: false });
        window.addEventListener('touchmove', preventPinch, { passive: false });
        window.addEventListener('touchend', preventDoubleTapZoom, { passive: false });
        window.addEventListener('wheel', preventWheelZoom, { passive: false });
        document.addEventListener('gesturestart', preventGesture);
        document.addEventListener('gesturechange', preventGesture);
        document.addEventListener('gestureend', preventGesture);

        this.zoomPreventionHandlers = {
            preventPinch,
            preventWheelZoom,
            preventGesture,
            preventDoubleTapZoom
        };
    }
    setupEventListeners() {
        // Start game button
        document.getElementById('startGame').addEventListener('click', () => {
            this.startGame();
        });

        // Window resize
        window.addEventListener('resize', () => {
            if (this.game) {
                this.game.handleResize();
            }
        });

        // Visibility change (pause when tab is hidden)
        document.addEventListener('visibilitychange', () => {
            if (this.game) {
                if (document.hidden) {
                    this.game.pause();
                } else {
                    this.game.resume();
                }
            }
        });
    }

    startGame() {
        if (this.game && this.isInitialized) {
            this.uiManager.hideMenu();
            this.uiManager.showHUD();
            this.uiManager.showGameStatus();
            this.uiManager.updateGameStatus({
                kills: 0,
                gold: 0,
                enemies: this.game.config.maxEnemies
            });
            if (isMobileDevice() && this.uiManager.hasJoystick()) {
                this.uiManager.showJoystick();
            }
            if (this.game?.mobileStatsOverlay && typeof this.game.mobileStatsOverlay.show === 'function') {
                this.game.mobileStatsOverlay.show();
            }
            this.game.start();
        }
    }

    // Called when game ends
    onGameEnd(stats) {
        this.uiManager.hideHUD();
        this.uiManager.hideGameStatus();
        this.uiManager.showGameOver(stats);
        if (this.uiManager.hasJoystick()) {
            this.uiManager.hideJoystick();
        }
        if (this.game?.mobileStatsOverlay && typeof this.game.mobileStatsOverlay.hide === 'function') {
            this.game.mobileStatsOverlay.hide();
        }
        if (this.game && this.game.inputManager) {
            this.game.inputManager.resetJoystick();
        }
    }

    // Called to return to main menu
    returnToMenu() {
        if (this.game) {
            this.game.stop();
            if (this.game.inputManager) {
                this.game.inputManager.resetJoystick();
            }
        }
        this.uiManager.hideAll();
        this.uiManager.hideGameStatus();
        if (this.uiManager.hasJoystick()) {
            this.uiManager.hideJoystick();
        }
        if (this.game?.mobileStatsOverlay && typeof this.game.mobileStatsOverlay.hide === 'function') {
            this.game.mobileStatsOverlay.hide();
        }
        this.uiManager.showMenu();
    }
}

// Initialize game when page loads
document.addEventListener('DOMContentLoaded', () => {
    const app = new GameApp();
    app.init();

    // Make app globally accessible for debugging
    window.gameApp = app;
});

// Export for potential module usage
export { GameApp };


